import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { MeetingDocDialog } from "@/components/meeting-docs/MeetingDocDialog";
import { Users, Search, X, Loader2, Building2, Briefcase, CheckCircle2, CalendarDays, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { VisibilityControls } from "@/components/common/Visibility";

const Card = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3" data-testid={testId}>
    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-brand" />}{title}</h3>
    {children}
  </div>
);

export default function DocumentMeeting() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id") || "";

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(!!editId);
  const [saving, setSaving] = useState(false);
  const [savedDoc, setSavedDoc] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mtype, setMtype] = useState("district_meeting");
  const [iv, setIv] = useState("group"); const [pv, setPv] = useState("internal_only");
  const [attendees, setAttendees] = useState([]); // {person_id,name,email,classification,organization_name}
  const [notes, setNotes] = useState("");
  const [actions, setActions] = useState("");
  const [agency, setAgency] = useState(null); // {id,name}
  const [opp, setOpp] = useState(null); // {id,label}

  // link to existing scheduled meeting
  const [meetings, setMeetings] = useState([]);
  const [mq, setMq] = useState("");
  const [linked, setLinked] = useState(null);
  const [occurred, setOccurred] = useState(false);
  const prefillPid = params.get("person_id") || "";
  const [pendingLinkId, setPendingLinkId] = useState("");

  // attendee search
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // agency / opp search
  const [aq, setAq] = useState("");
  const [aRes, setARes] = useState([]);
  const [oq, setOq] = useState("");
  const [oRes, setORes] = useState([]);

  useEffect(() => {
    api.get("/meeting-docs/meta").then((r) => setTypes(r.data.meeting_types || [])).catch((e) => toast.error(apiError(e)));
    api.get("/meeting-docs/schedulable-meetings", { params: prefillPid ? { person_id: prefillPid } : {} }).then((r) => setMeetings(r.data.items || [])).catch(() => setMeetings([]));
  }, []);

  useEffect(() => {
    if (!pendingLinkId || !meetings.length) return;
    const m = meetings.find((x) => x.id === pendingLinkId);
    if (m) { setLinked(m); setPendingLinkId(""); }
  }, [pendingLinkId, meetings]);

  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    api.get(`/meeting-docs/${editId}`).then((r) => {
      const d = r.data;
      setTitle(d.title || ""); setDate(d.meeting_date || ""); setTime(d.meeting_time || "");
      setMtype(d.meeting_type || "other"); setAttendees(d.attendees || []);
      setNotes(d.notes_html || ""); setActions(d.action_items_html || "");
      setIv(d.internal_visibility || "group"); setPv(d.portal_visibility || "internal_only");
      setPendingLinkId(d.related_meeting_id || "");
    }).catch((e) => { toast.error(apiError(e)); navigate("/calendar/document-meeting"); })
      .finally(() => setLoading(false));
  }, [editId]);

  // debounced attendee search
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/persons", { params: { q, page_size: 8, all_districts: true } });
        setResults(data.persons || []);
      } catch (e) { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (aq.trim().length < 2) { setARes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/organizations", { params: { q: aq, page_size: 6 } }); setARes(data.organizations || data.items || []); }
      catch { setARes([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [aq]);

  useEffect(() => {
    if (oq.trim().length < 2) { setORes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/opportunities", { params: { q: oq, page_size: 6 } }); setORes(data.opportunities || []); }
      catch { setORes([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [oq]);

  const addAttendee = (p) => {
    if (attendees.some((a) => a.person_id === p.id)) return;
    setAttendees((s) => s.some((a) => a.person_id === p.id) ? s : [...s, { person_id: p.id, name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
      email: p.email, classification: (p.classifications || [])[0], organization_name: p.organization_name }]);
    setQ(""); setResults([]);
  };
  const removeAttendee = (pid) => setAttendees((s) => s.filter((a) => a.person_id !== pid));
  useEffect(() => {
    if (!prefillPid || editId) return;
    api.get(`/persons/${prefillPid}`).then((r) => { const p = r.data.person || r.data; if (p?.id) addAttendee(p); }).catch(() => {});
  }, [prefillPid]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectMeeting = (m) => {
    setLinked(m);
    if (!title.trim() && m.title) setTitle(m.title);
    if (m.start) { setDate((d) => d || m.start.slice(0, 10)); setTime((t) => t || m.start.slice(11, 16)); }
    if (m.attendee && !attendees.some((a) => a.person_id === m.attendee.person_id)) setAttendees((s) => [...s, m.attendee]);
    setMq("");
  };
  const unlink = () => setLinked(null);

  const save = async () => {
    if (!title.trim()) { toast.error("Meeting Title is required"); return; }
    if (!date) { toast.error("Meeting Date is required"); return; }
    if (!attendees.length) { toast.error("Add at least one attendee"); return; }
    if (!notes || !notes.replace(/<[^>]+>/g, "").trim()) { toast.error("Meeting Notes are required"); return; }
    setSaving(true);
    const body = { title: title.trim(), meeting_date: date, meeting_time: time || null, meeting_type: mtype,
      attendee_ids: attendees.map((a) => a.person_id), notes_html: notes, action_items_html: actions,
      related_agency_id: agency?.id || null, related_opportunity_id: opp?.id || null,
      related_meeting_id: linked?.id || null, internal_visibility: iv, portal_visibility: pv, outcome_status: occurred ? "completed" : null };
    try {
      const { data } = editId ? await api.patch(`/meeting-docs/${editId}`, body) : await api.post("/meeting-docs", body);
      setSavedDoc(data);
      toast.success(`Meeting documented for ${data.attendees?.length || attendees.length} attendee${(data.attendees?.length || attendees.length) === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resetForm = () => {
    navigate("/calendar/document-meeting");
    setSavedDoc(null); setTitle(""); setDate(""); setTime(""); setMtype("district_meeting");
    setAttendees([]); setNotes(""); setActions(""); setAgency(null); setOpp(null);
    setLinked(null); setMq(""); setPendingLinkId("");
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  if (savedDoc) {
    return (
      <div data-testid="document-meeting-success" className="max-w-xl">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-lg font-semibold text-slate-800">Meeting documented</h2>
          <p className="text-sm text-slate-600 mt-1">
            “{savedDoc.title}” is now in the history of {savedDoc.attendees?.length || attendees.length} attendee{(savedDoc.attendees?.length || attendees.length) === 1 ? "" : "s"}.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Button variant="outline" onClick={() => setViewOpen(true)} data-testid="dm-view-meeting"><CalendarDays className="w-4 h-4 mr-1.5" /> View Meeting</Button>
            <Button className="bg-brand hover:bg-brand-strong" onClick={resetForm} data-testid="dm-document-another">Document Another</Button>
            <Button variant="ghost" onClick={() => navigate("/calendar")} data-testid="dm-done">Done</Button>
          </div>
        </div>
        <MeetingDocDialog docId={savedDoc.id} open={viewOpen} onClose={() => setViewOpen(false)} />
      </div>
    );
  }

  return (
    <div data-testid="document-meeting-page" className="max-w-3xl space-y-5">
      <PageHeader title={editId ? "Edit Meeting Documentation" : "Document a Meeting"}
        description="Record notes for a meeting that included one or more People. One shared record appears in every attendee's history." />

      <Card title="Link a Scheduled Meeting (optional)" icon={CalendarClock} testId="dm-link-card">
        {linked ? (
          <div className="rounded-lg border border-brand/30 bg-brand/5 p-3" data-testid="dm-linked-meeting">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-slate-800">{linked.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{linked.start ? new Date(linked.start).toLocaleString() : ""}{linked.timezone ? ` · ${linked.timezone}` : ""}</p>
                <p className="text-xs text-slate-500">Host: {linked.host_name || "—"}{linked.attendee ? ` · Attendee: ${linked.attendee.name}` : ""}</p>
              </div>
              <button type="button" onClick={unlink} data-testid="dm-unlink" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Documentation attaches to this meeting — no new calendar event or Teams meeting is created.</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500">Documenting a meeting that was already scheduled in Top-Recruit? Link it to prefill the details. Otherwise just fill in the form below for an impromptu meeting.</p>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={mq} onChange={(e) => setMq(e.target.value)} placeholder="Search your scheduled meetings…" className="pl-8" data-testid="dm-meeting-search" />
            </div>
            <div className="space-y-1.5 max-h-56 overflow-auto" data-testid="dm-meeting-list">
              {meetings.filter((m) => !mq || (m.title || "").toLowerCase().includes(mq.toLowerCase())).slice(0, 25).map((m) => (
                <button key={m.id} type="button" onClick={() => selectMeeting(m)} data-testid={`dm-meeting-opt-${m.id}`}
                  className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors">
                  <span className="text-sm font-medium text-slate-800">{m.title}</span>
                  <span className="block text-xs text-slate-500">{m.start ? new Date(m.start).toLocaleString() : ""} · Host {m.host_name || "—"}{m.attendee ? ` · ${m.attendee.name}` : ""}</span>
                </button>
              ))}
              {meetings.length === 0 && <p className="text-xs text-slate-400">No scheduled meetings found — document an impromptu meeting below.</p>}
            </div>
          </>
        )}
      </Card>

      <Card title="Meeting Information" icon={CalendarDays} testId="dm-info-card">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Meeting Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. District 88 Agent Meeting" className="mt-1" data-testid="dm-title" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Meeting Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="dm-date" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Time (optional)</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="dm-time" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Meeting Type</Label>
            <Select value={mtype} onValueChange={setMtype}>
              <SelectTrigger className="mt-1" data-testid="dm-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {types.map((t) => <SelectItem key={t.code} value={t.code} data-testid={`dm-type-${t.code}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Attendees *" icon={Users} testId="dm-attendees-card">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all People by name or email…" className="pl-8" data-testid="dm-attendee-search" />
          {searching && <p className="text-xs text-slate-400 mt-1">Searching…</p>}
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-64 overflow-auto" data-testid="dm-attendee-results">
              {results.map((p) => (
                <button key={p.id} type="button" onClick={() => addAttendee(p)} data-testid={`dm-attendee-opt-${p.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                  <span className="text-sm font-medium text-slate-800">{p.first_name} {p.last_name}</span>
                  <span className="block text-xs text-slate-500">{p.email}{(p.classifications || [])[0] ? ` · ${(p.classifications || [])[0]}` : ""}{p.organization_name ? ` · ${p.organization_name}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {attendees.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="dm-attendee-chips">
            {attendees.map((a) => (
              <span key={a.person_id} className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 text-brand px-2.5 py-1 text-xs font-medium" data-testid={`dm-attendee-chip-${a.person_id}`}>
                {a.name}{a.classification ? <span className="opacity-60">· {a.classification}</span> : null}
                <button type="button" onClick={() => removeAttendee(a.person_id)} data-testid={`dm-attendee-remove-${a.person_id}`}><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">No attendees added yet.</p>}
      </Card>

      <Card title="Related (optional)" icon={Briefcase} testId="dm-related-card">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="relative">
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Related Agency</Label>
            {agency ? (
              <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                {agency.name}<button type="button" onClick={() => setAgency(null)} data-testid="dm-agency-clear"><X className="w-4 h-4 text-slate-400" /></button>
              </div>
            ) : (
              <>
                <Input value={aq} onChange={(e) => setAq(e.target.value)} placeholder="Search agencies…" className="mt-1" data-testid="dm-agency-search" />
                {aRes.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-auto">
                    {aRes.map((o) => (
                      <button key={o.id} type="button" onClick={() => { setAgency({ id: o.id, name: o.name }); setAq(""); setARes([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`dm-agency-opt-${o.id}`}>{o.name}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="relative">
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Related Opportunity</Label>
            {opp ? (
              <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                {opp.label}<button type="button" onClick={() => setOpp(null)} data-testid="dm-opp-clear"><X className="w-4 h-4 text-slate-400" /></button>
              </div>
            ) : (
              <>
                <Input value={oq} onChange={(e) => setOq(e.target.value)} placeholder="Search opportunities…" className="mt-1" data-testid="dm-opp-search" />
                {oRes.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-auto">
                    {oRes.map((o) => (
                      <button key={o.id} type="button" onClick={() => { setOpp({ id: o.id, label: `${o.person_name || o.human_id}${o.pipeline_name ? ` · ${o.pipeline_name}` : ""}` }); setOq(""); setORes([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`dm-opp-opt-${o.id}`}>
                        {o.person_name || o.human_id}{o.pipeline_name ? ` · ${o.pipeline_name}` : ""}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      <Card title="Meeting Notes *" testId="dm-notes-card">
        <RichTextEditor value={notes} onChange={setNotes} testId="dm-notes" minHeight={200} placeholder="Write the meeting notes…" />
      </Card>

      <Card title="Visibility" testId="dm-visibility-card">
        <VisibilityControls internal={iv} portal={pv} onInternal={setIv} onPortal={setPv} who="attendees" testPrefix="dm-vis" />
        <p className="text-xs text-slate-500 mt-2">Meeting notes stay Internal Only by default. Attending the meeting does not give attendees access. Turn sharing on only if these notes are written for the attendees. Otherwise, add a separate note with a summary and share that note.</p>
      </Card>

      <Card title="Action Items / Follow-Up (optional)" testId="dm-actions-card">
        <RichTextEditor value={actions} onChange={setActions} testId="dm-actions" minHeight={120} placeholder="Add any follow-up items…" />
      </Card>

      <label className="flex items-center gap-2 text-sm text-slate-700 justify-end" data-testid="dm-occurred-label">
        <input type="checkbox" checked={occurred} onChange={(e) => setOccurred(e.target.checked)} data-testid="dm-occurred" />
        This meeting took place (mark completed). Saving notes alone never completes a scheduled meeting. No invitation is sent.
      </label>
      <div className="flex items-center justify-end gap-2 pb-10">
        <Button variant="ghost" onClick={() => navigate("/calendar")} data-testid="dm-cancel">Cancel</Button>
        <Button className="bg-brand hover:bg-brand-strong" disabled={saving} onClick={save} data-testid="dm-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}{editId ? "Save Changes" : "Save Meeting"}
        </Button>
      </div>
    </div>
  );
}
